import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import Button from '@/components/button/Button';
import FormCheckbox from '@/components/FormCheckbox';

import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconSuccessful16 } from '@cineamo/ui/src/icons/ic-successful';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { track } from '@/utils/server-side-tracking';

import FormInput from './FormInput';
import { sendEmailBirthdayAtTheCinema } from './send-email';
import TurnstileWidget from './TurnstileWidget';

export type BirthdayAtTheCinemaFormData = {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    childBirthday: string;
    cinemaVisitDate: string;
    formattedCinemaVisitDate?: string;
    movieWish: string;
    birthdayPackageInfo: boolean;
    childrenCount: number;
    adultsCount: number;
};

export default function BirthdayAtTheCinemaForm() {
    const t = useTranslations();
    const { dateTime } = useFormat();
    const domainConfiguration = useDomainConfiguration();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    const { register, handleSubmit, watch, setValue, reset } = useForm<BirthdayAtTheCinemaFormData>({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            childBirthday: '',
            cinemaVisitDate: '',
            movieWish: '',
            birthdayPackageInfo: false,
            childrenCount: undefined,
            adultsCount: undefined
        }
    });

    const birthdayPackageInfo = watch('birthdayPackageInfo');

    const onSubmit = async (data: BirthdayAtTheCinemaFormData) => {
        setIsProcessing(true);

        try {
            const emailData: BirthdayAtTheCinemaFormData = {
                ...data,
                formattedCinemaVisitDate: data.cinemaVisitDate
                    ? dateTime.formatDateAndTime(data.cinemaVisitDate)
                    : undefined
            };
            const success = await sendEmailBirthdayAtTheCinema(emailData, turnstileToken ?? undefined);

            if (success) {
                setIsSuccess(true);
                reset();
                showToast({
                    toastType: toast.success,
                    messageIcon: <IconSuccessful16 />,
                    messageTitle: t('birthday-at-the-cinema.success.title'),
                    messageBody: t('birthday-at-the-cinema.success.message'),
                    backgroundColor: domainConfiguration.style.primaryColor,
                    textColor: domainConfiguration.style.onPrimaryColor
                });
            } else {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('birthday-at-the-cinema.error.title'),
                    messageBody: t('birthday-at-the-cinema.error.message')
                });
            }

            track('Birthday at the Cinema Form Submitted', {
                cinema: domainConfiguration.cinema?.name,
                domain: domainConfiguration.domain,
                cinemaId: domainConfiguration.cinema?.id,
                data: JSON.stringify(data, null, 2),
                success: success
            });
        } catch {
            showToast({
                toastType: toast.error,
                messageTitle: t('birthday-at-the-cinema.error.title'),
                messageBody: t('birthday-at-the-cinema.error.network-message')
            });
        } finally {
            setIsProcessing(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="backdrop-blur-sm rounded-12 p-12 shadow-lg text-center bg-[var(--surface-color)]">
                <IconSuccessful16 className="size-[48px] mx-auto mb-4 text-[var(--primary-color)]" />
                <h3 className="text-headline-24 mb-2 text-[var(--on-background-color)]">
                    {t('birthday-at-the-cinema.success.title')}
                </h3>
                <p className="text-body-16 text-[rgba(var(--on-background-color-rgb),0.8)]">
                    {t('birthday-at-the-cinema.success.message')}
                </p>
            </div>
        );
    }

    return (
        <div className="backdrop-blur-sm rounded-12 p-12 shadow-lg bg-[var(--surface-color)]">
            <form
                className="space-y-24"
                onSubmit={handleSubmit(onSubmit)}>
                {/* Contact Information */}
                <div className="grid md:grid-cols-2 gap-6">
                    <FormInput
                        name="firstName"
                        label={t('birthday-at-the-cinema.form.first-name')}
                        type="text"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                    />
                    <FormInput
                        name="lastName"
                        label={t('birthday-at-the-cinema.form.last-name')}
                        type="text"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <FormInput
                        name="email"
                        label={t('birthday-at-the-cinema.form.email')}
                        type="email"
                        register={register}
                        registerOptions={{ required: true }}
                        required
                    />
                    <FormInput
                        name="phone"
                        label={t('birthday-at-the-cinema.form.phone')}
                        type="tel"
                        register={register}
                    />
                </div>

                {/* Child's Birthday */}
                <FormInput
                    name="childBirthday"
                    label={t('birthday-at-the-cinema.form.child-birthday')}
                    type="date"
                    register={register}
                    registerOptions={{ required: true }}
                    required
                />

                {/* Cinema Visit Date and Time */}
                <FormInput
                    name="cinemaVisitDate"
                    label={t('birthday-at-the-cinema.form.cinema-visit-date')}
                    type="datetime-local"
                    register={register}
                    registerOptions={{ required: true }}
                    required
                />

                {/* Movie Wish */}
                <FormInput
                    name="movieWish"
                    label={t('birthday-at-the-cinema.form.movie-wish')}
                    type="text"
                    register={register}
                    registerOptions={{ required: true }}
                    required
                />

                {/* Birthday Package Info Checkbox */}
                <FormCheckbox
                    name="birthday-package-info"
                    label={t('birthday-at-the-cinema.form.birthday-package-info')}
                    checked={birthdayPackageInfo}
                    onChange={(checked) => setValue('birthdayPackageInfo', checked)}
                />

                {/* Guest Count */}
                <div className="space-y-4">
                    <label className="block text-body-14 font-semibold text-[var(--on-surface-color)]">
                        {t('birthday-at-the-cinema.form.guest-count')}
                    </label>
                    <div className="grid md:grid-cols-2 gap-6">
                        <FormInput
                            name="childrenCount"
                            label={t('birthday-at-the-cinema.form.children-count')}
                            type="number"
                            inputMode="numeric"
                            min={1}
                            register={register}
                            registerOptions={{ valueAsNumber: true, required: true }}
                            required
                        />
                        <FormInput
                            name="adultsCount"
                            label={t('birthday-at-the-cinema.form.adults-count')}
                            type="number"
                            inputMode="numeric"
                            min={1}
                            register={register}
                            registerOptions={{ valueAsNumber: true, required: true }}
                            required
                        />
                    </div>
                </div>

                {/* Separator */}
                <div className="w-full h-1 bg-[rgba(var(--on-surface-color-rgb),0.3)]" />

                {/* Required Fields Note */}
                <div className="pt-4">
                    <p className="text-body-12 text-center text-[rgba(var(--on-surface-color-rgb),0.7)]">
                        {t('birthday-at-the-cinema.form.required-fields')}
                    </p>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                    <TurnstileWidget onVerify={setTurnstileToken} />
                    <Button
                        type="submit"
                        disabled={isProcessing}
                        className="w-full py-4 text-body-16 font-semibold transition-all duration-200 rounded-8"
                        css={{
                            backgroundColor: domainConfiguration.style.primaryColor,
                            color: domainConfiguration.style.onPrimaryColor,
                            opacity: isProcessing ? 0.6 : 1
                        }}
                        leadingIcon={isProcessing ? <IconLoading16 className="animate-spin" /> : undefined}>
                        {isProcessing
                            ? t('birthday-at-the-cinema.form.submitting')
                            : t('birthday-at-the-cinema.form.submit')}
                    </Button>
                </div>
            </form>
        </div>
    );
}
